/**
 * Tervezet leadás Drawer — közös komponens.
 *
 * - Megjeleníti a kiválasztott napot
 * - Listázza a dolgozó engedélyezett sablonjait
 * - Tap egy sablonon → opcionális megjegyzés → mentés
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Info, Clock, MapPin, Moon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useLocalizedDates } from "./i18n/dates";
import { localizeTemplateName } from "./i18n/shiftNames";

const TEMPLATE_COLOR_BG = {
  indigo: "bg-indigo-50 ring-indigo-200 dark:bg-indigo-950/30 dark:ring-indigo-900/40",
  emerald: "bg-emerald-50 ring-emerald-200 dark:bg-emerald-950/30 dark:ring-emerald-900/40",
  amber: "bg-amber-50 ring-amber-200 dark:bg-amber-950/30 dark:ring-amber-900/40",
  rose: "bg-rose-50 ring-rose-200 dark:bg-rose-950/30 dark:ring-rose-900/40",
  sky: "bg-sky-50 ring-sky-200 dark:bg-sky-950/30 dark:ring-sky-900/40",
  violet: "bg-violet-50 ring-violet-200 dark:bg-violet-950/30 dark:ring-violet-900/40",
  teal: "bg-teal-50 ring-teal-200 dark:bg-teal-950/30 dark:ring-teal-900/40",
  fuchsia: "bg-fuchsia-50 ring-fuchsia-200 dark:bg-fuchsia-950/30 dark:ring-fuchsia-900/40",
  orange: "bg-orange-50 ring-orange-200 dark:bg-orange-950/30 dark:ring-orange-900/40",
  slate: "bg-slate-50 ring-slate-200 dark:bg-slate-800/40 dark:ring-slate-700",
};
const TEMPLATE_DOT = {
  indigo: "bg-indigo-500",
  emerald: "bg-emerald-500",
  amber: "bg-amber-500",
  rose: "bg-rose-500",
  sky: "bg-sky-500",
  violet: "bg-violet-500",
  teal: "bg-teal-500",
  fuchsia: "bg-fuchsia-500",
  orange: "bg-orange-500",
  slate: "bg-slate-500",
};

const fmtDate = (iso, days, months) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return `${d.getFullYear()}. ${months[d.getMonth()]} ${d.getDate()}. (${days[d.getDay()]})`;
};

const DAY_OFF_KEY = "__day_off__";

export function DraftPickerDrawer({ open, onOpenChange, date, templates, existingDraft, onSaved }) {
  const { t } = useTranslation();
  const { days, months } = useLocalizedDates();
  const [selectedId, setSelectedId] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (existingDraft?.is_day_off) {
        setSelectedId(DAY_OFF_KEY);
      } else {
        setSelectedId(existingDraft?.template_id || null);
      }
      setNotes(existingDraft?.notes || "");
    }
  }, [open, existingDraft]);

  const onSubmit = async () => {
    if (!date || !selectedId) {
      toast.error(t("drafts.pick_template_error"));
      return;
    }
    setSubmitting(true);
    try {
      const isDayOff = selectedId === DAY_OFF_KEY;
      await api.post("/portal/shift-drafts", {
        date,
        template_id: isDayOff ? null : selectedId,
        is_day_off: isDayOff,
        notes: notes || null,
      });
      toast.success(existingDraft ? t("drafts.modified_toast") : t("drafts.added_toast"));
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        data-testid="draft-picker-drawer"
        className="flex max-h-[88dvh] flex-col rounded-t-3xl border-0 bg-zinc-50 p-0 dark:bg-zinc-950"
      >
        <DrawerHeader className="shrink-0 px-5 pb-2 pt-3 text-left">
          <DrawerTitle className="text-[17px] font-semibold tracking-tight">
            {t("drafts.drawer_title")}
          </DrawerTitle>
          {date && (
            <p className="mt-0.5 text-[12.5px] text-zinc-500">{fmtDate(date, days, months)}</p>
          )}
        </DrawerHeader>

        {/* Warning banner */}
        <div className="shrink-0 px-5 pt-1">
          <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 px-3.5 py-2.5 ring-1 ring-amber-200 dark:bg-amber-950/30 dark:ring-amber-900/40">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" strokeWidth={2.2} />
            <p className="text-[11.5px] leading-snug text-amber-800 dark:text-amber-200">
              {t("drafts.not_final_body")}
            </p>
          </div>
        </div>

        {/* Templates list */}
        <div className="flex-1 space-y-2 overflow-y-auto px-5 pb-3 pt-3">
          <p className="px-1 text-[12px] font-medium uppercase tracking-wide text-zinc-400">
            {t("drafts.pick_option")}
          </p>

          {/* Szabadnap kérés — mindig elérhető */}
          <button
            type="button"
            data-testid="template-day-off"
            onClick={() => setSelectedId(DAY_OFF_KEY)}
            className={
              "flex w-full items-center gap-3 rounded-2xl px-4 py-3.5 text-left transition-all " +
              (selectedId === DAY_OFF_KEY
                ? "bg-white shadow-[0_4px_14px_-4px_rgba(59,130,246,0.25)] ring-2 ring-blue-500 dark:bg-zinc-900"
                : "bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] hover:bg-zinc-50 dark:bg-zinc-900 dark:hover:bg-zinc-800/40")
            }
          >
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:ring-slate-700">
              <Moon className="h-4 w-4" strokeWidth={2} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[14.5px] font-semibold tracking-tight">{t("drafts.day_off")}</p>
              <p className="mt-0.5 text-[12px] text-zinc-500">
                {t("drafts.day_off_subtitle")}
              </p>
            </div>
            {selectedId === DAY_OFF_KEY && (
              <Check className="h-5 w-5 shrink-0 text-blue-500" strokeWidth={2.5} />
            )}
          </button>

          {templates.length === 0 ? (
            <div className="rounded-2xl bg-white p-6 text-center shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900">
              <p className="text-[13px] text-zinc-500">{t("drafts.no_choices")}</p>
            </div>
          ) : (
            templates.map((tpl) => {
              const active = selectedId === tpl.id;
              return (
                <button
                  key={tpl.id}
                  type="button"
                  data-testid={`template-${tpl.id}`}
                  onClick={() => setSelectedId(tpl.id)}
                  className={
                    "flex w-full items-center gap-3 rounded-2xl px-4 py-3.5 text-left transition-all " +
                    (active
                      ? "bg-white shadow-[0_4px_14px_-4px_rgba(59,130,246,0.25)] ring-2 ring-blue-500 dark:bg-zinc-900"
                      : "bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] hover:bg-zinc-50 dark:bg-zinc-900 dark:hover:bg-zinc-800/40")
                  }
                >
                  <span
                    className={
                      "grid h-10 w-10 shrink-0 place-items-center rounded-2xl ring-1 ring-inset " +
                      (TEMPLATE_COLOR_BG[tpl.color] || TEMPLATE_COLOR_BG.indigo)
                    }
                  >
                    <span className={"h-2 w-2 rounded-full " + (TEMPLATE_DOT[tpl.color] || TEMPLATE_DOT.indigo)} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[14.5px] font-semibold tracking-tight">{localizeTemplateName(tpl.name, t)}</p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[12px] text-zinc-500">
                      <span className="font-mono tabular-nums">{tpl.start_time}–{tpl.end_time}</span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" strokeWidth={2} />
                        {Number(tpl.duration_hours || 0).toFixed(1)}h
                      </span>
                      {tpl.site_name && (
                        <span className="inline-flex items-center gap-1 truncate">
                          <MapPin className="h-3 w-3" strokeWidth={2} />
                          {tpl.site_name}
                        </span>
                      )}
                    </div>
                  </div>
                  {active && (
                    <Check className="h-5 w-5 shrink-0 text-blue-500" strokeWidth={2.5} />
                  )}
                </button>
              );
            })
          )}

          {/* Notes */}
          <div className="mt-3 overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900">
            <textarea
              rows={2}
              data-testid="draft-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("drafts.notes_placeholder")}
              className="block w-full resize-none bg-transparent px-4 py-3 text-[16px] text-zinc-900 placeholder:text-zinc-400 focus:outline-none dark:text-white"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-zinc-200/60 bg-white/95 px-4 pb-5 pt-3 backdrop-blur dark:border-zinc-800 dark:bg-zinc-900/95">
          <button
            type="button"
            data-testid="draft-submit"
            onClick={onSubmit}
            disabled={submitting || !selectedId}
            className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-blue-500 text-[14.5px] font-semibold text-white shadow-[0_4px_14px_-4px_rgba(59,130,246,0.5)] transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-zinc-200 disabled:text-zinc-400 disabled:shadow-none dark:disabled:bg-zinc-800 dark:disabled:text-zinc-600"
          >
            {submitting ? t("common.saving") : (
              <>
                <Check className="h-4 w-4" strokeWidth={2.5} />
                {existingDraft ? t("drafts.submit_edit") : t("drafts.submit_new")}
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="mt-1.5 inline-flex h-10 w-full items-center justify-center rounded-2xl text-[13.5px] font-medium text-zinc-500 hover:text-zinc-900 dark:hover:text-white"
          >
            {t("common.cancel")}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
